
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function NewCommercialBuildsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Electrical Systems for New Commercial Builds',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Complete electrical systems for new commercial construction in Fresno CA from design through final inspection and occupancy',
    url: `${siteUrl}/services/commercial-specialist/new-commercial-builds`,
  };

  const features = [
    {
      icon: 'ri-pencil-ruler-2-line',
      title: 'Electrical Design Services',
      description: 'Complete electrical design and engineering for new commercial buildings',
    },
    {
      icon: 'ri-building-line',
      title: 'Ground-Up Construction',
      description: 'Full electrical installation for new commercial construction projects',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Service & Distribution',
      description: 'Main service installation, panels, and power distribution systems',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Complete Lighting Systems',
      description: 'Interior and exterior lighting design and installation',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Permit & Inspection Management',
      description: 'Handling all electrical permits and inspection coordination',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Project Coordination',
      description: 'Working with general contractors and other trades for on-time completion',
    },
  ];

  return (
    <>
      <SEOHead
        title="New Commercial Construction Electrical Fresno | Building Electrician CA"
        description="Complete electrical systems for new commercial construction in Fresno CA. Design, installation, and project management for commercial buildings. Licensed C-10 contractors."
        canonical="/services/commercial-specialist/new-commercial-builds"
        keywords="new commercial construction electrical Fresno, commercial building electrician, construction electrical, commercial electrical contractor"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Electrical Systems for New Commercial Builds — Fresno, CA"
        heroSubtitle="Complete electrical solutions for new commercial construction projects throughout Fresno"
        heroImage="https://public.readdy.ai/ai/img_res/77efe08a6a725a0e4442574b88515303.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Commercial%20construction%20site%20with%20electrical%20panel%20installation%20and%20organized%20conduit%20runs%20in%20new%20building%20with%20professional%20electrical%20work%20in%20progress&width=700&height=600&seq=new-comm-fresno-001&orientation=portrait"
        fresnoContent="Building Fresno's commercial future with expert electrical construction services. From office buildings to retail centers, restaurants to industrial facilities throughout Fresno County, we provide complete electrical systems for new commercial construction. Our licensed electricians work with developers, general contractors, and architects from design through final inspection, delivering code-compliant electrical installations on schedule and within budget. We understand the critical importance of coordination and timing in commercial construction—our experienced team ensures electrical work progresses smoothly without delaying your Fresno project."
        features={features}
        faqs={[
          {
            question:
              'What electrical services are included for new commercial construction in Fresno?',
            answer:
              'Our new commercial construction electrical services in Fresno CA include complete electrical design and engineering, utility coordination and service installation, main electrical panels and distribution systems, interior and exterior lighting systems, power distribution and outlet installation, three-phase power for equipment, fire alarm and life safety systems, emergency lighting and exit signs, data and communication infrastructure electrical, HVAC electrical connections, parking lot and exterior lighting, permit management and inspection coordination, and final testing and commissioning. We provide turnkey electrical solutions for offices, retail buildings, restaurants, warehouses, medical facilities, and all commercial construction throughout Fresno.',
          },
          {
            question: 'How much does electrical cost for new commercial construction in Fresno?',
            answer:
              'New commercial construction electrical costs in Fresno typically range from $8-$20 per square foot depending on building type and complexity. A 5,000 sq ft office building averages $40,000-$100,000 for complete electrical. A 10,000 sq ft retail building ranges from $80,000-$200,000. Restaurants and buildings with heavy equipment loads cost $15-$25+ per square foot. Warehouses with basic lighting average $5-$10 per square foot. Costs include design, materials, labor, permits, and inspections. We provide detailed electrical estimates during the design phase based on building plans and specifications. Early electrical planning prevents costly changes during construction.',
          },
          {
            question: 'When should electrical contractors be involved in Fresno commercial projects?',
            answer:
              'Electrical contractors should be involved early in Fresno commercial construction projects—ideally during the design phase. Early involvement allows us to provide electrical design input and value engineering, coordinate utility service requirements with PG&E, identify potential issues before construction begins, provide accurate cost estimates for budgeting, plan electrical infrastructure that supports building operations, ensure designs meet California Title 24 energy codes, and coordinate with architects and other trades. Bringing electricians in early prevents costly design changes, ensures adequate electrical capacity, and keeps projects on schedule. We work with developers and general contractors throughout Fresno from concept through completion.',
          },
          {
            question: 'How long does electrical installation take for new commercial buildings in Fresno?',
            answer:
              'Commercial electrical installation timelines in Fresno vary by building size and complexity. Small commercial buildings (2,000-5,000 sq ft) typically require 3-6 weeks for electrical work. Medium buildings (5,000-20,000 sq ft) average 6-12 weeks. Large commercial buildings or multi-story construction may require 3-9 months for complete electrical installation. Electrical work progresses in phases coordinated with other trades—rough-in during framing, service and panel installation, lighting and finish work, and final testing. We provide detailed schedules and coordinate closely with general contractors to keep Fresno commercial projects on track.',
          },
          {
            question: 'Do you coordinate with other trades on Fresno commercial projects?',
            answer:
              'Yes, coordination with other trades is essential for successful commercial construction in Fresno. We work closely with general contractors to schedule electrical work around framing, drywall, HVAC, plumbing, and other trades. We coordinate with HVAC contractors for equipment electrical connections, with low-voltage contractors for data and communication systems, with fire alarm contractors for life safety systems, and with utility companies for service installation. Our experienced project managers attend regular coordination meetings and maintain communication with all trades to prevent conflicts and keep projects on schedule. We understand commercial construction workflows and ensure electrical work integrates smoothly with all aspects of your Fresno building project.',
          },
          {
            question: 'Are you licensed for commercial construction electrical work in Fresno?',
            answer:
              "Yes, we hold a California C-10 Electrical Contractor license required for all commercial construction electrical work in Fresno CA. Our electricians are trained in commercial electrical systems, construction electrical practices, California Title 24 compliance, and NEC codes. We carry full commercial general liability insurance and workers' compensation coverage. We pull all required electrical permits through the City of Fresno Building Department and coordinate all inspections for code compliance. Our team has extensive experience with commercial construction projects throughout Fresno and Central California, working with major developers, general contractors, and architects on successful commercial building projects.",
          },
        ]}
      />
    </>
  );
}
